import React, { useCallback, useRef, useState } from 'react';

import Button from 'components/shared/Button';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { renderModal } from 'utils/modal_util';

import crops from 'domains/crops';
import photoModule, { PhotoUpload } from 'domains/photos';

import CollectionsModal from '../CollectionsModal';
import CollectionPhotoTile from './CollectionPhotoTile';
import DeletePhotoModal from './DeletePhotoModal';
import MovePhotoModal from './MovePhotoModal';

import type { CollectionName } from 'domains/photos';

type SingleCollectionManagerProps = {
  displayCollection: CollectionName;
  onClose: (closeAll?: boolean) => void;
};

function SingleCollectionManager({ onClose, displayCollection }: SingleCollectionManagerProps) {
  const elem = useRef<HTMLUListElement>(null);

  const [pendingDeletePhoto, setPendingDeletePhoto] = useState<PhotoUpload | undefined>();
  const [pendingMovePhoto, setPendingMovePhoto] = useState<PhotoUpload | undefined>();

  const dispatch = useAppDispatch();
  const displayPhotos = useAppSelector((state) =>
    photoModule.selectors.allForCollection(state, displayCollection)
  );

  const collections = useAppSelector(photoModule.selectors.allNamedCollections);

  // FIXME: this is not working very well, should rework it
  const handleTileDrop = useCallback(
    (e: React.DragEvent) => {
      const eventData = e.dataTransfer.getData('text/plain');
      if (!eventData || !elem.current) return;

      let dropTile: PhotoUpload | undefined;

      try {
        dropTile = JSON.parse(eventData) as PhotoUpload;
      } catch {}

      if (dropTile) {
        const centerPoints = Array.from(elem.current.children).map((e2) => {
          const { x, y, height, width } = e2.getBoundingClientRect();
          return { x: x + width / 2, y: y + height };
        });
        const { clientX, clientY } = e;
        const dropBeforeIndex = centerPoints.findIndex((p) => p.x > clientX && p.y > clientY);
        dispatch(
          photoModule.actions.sortPhotos({
            insertPhotoId: dropTile.id,
            beforeIndex: dropBeforeIndex,
          })
        );
      }
    },
    [displayPhotos, dispatch]
  );

  function handleConfirmDeletePhoto(id: number) {
    dispatch(photoModule.actions.removePhoto.pending(id));
    setPendingDeletePhoto(undefined);

    if (displayPhotos.length === 1) {
      onClose();
    }
  }

  function handleConfirmMovePhoto(id: number, collection: string) {
    dispatch(photoModule.actions.update.pending({ id, collection }));
    setPendingMovePhoto(undefined);

    if (displayPhotos.length === 1) {
      onClose();
    }
  }

  function handleSelectPhoto(photo: PhotoUpload) {
    dispatch(crops.actions.applyPhotoToActiveNode(photo.id));
    onClose();
  }

  const buttons = (
    <React.Fragment>
      <Button
        type="outline"
        onClick={() => {
          dispatch(photoModule.actions.setActiveCollection(displayCollection));
          onClose(true);
        }}
      >
        Load Collection
      </Button>
      <Button type="outline" onClick={() => onClose()}>
        Back to Collections
      </Button>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <CollectionsModal
        title={displayCollection || 'Uncollected'}
        buttons={buttons}
        lastFocusedElementId="collections-menu-trigger"
        onClose={onClose}
      >
        <ul
          className="collections_manager_items modal-list-items animated fadeIn items sortable"
          ref={elem}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleTileDrop}
        >
          {displayPhotos.map((photo, tab) => (
            <CollectionPhotoTile
              key={photo.id}
              photo={photo}
              onSelect={() => handleSelectPhoto(photo)}
              onMove={() => setPendingMovePhoto(photo)}
              onDelete={() => setPendingDeletePhoto(photo)}
            />
          ))}
        </ul>
      </CollectionsModal>

      {pendingDeletePhoto &&
        renderModal(
          <DeletePhotoModal
            photo={pendingDeletePhoto}
            onConfirm={() => handleConfirmDeletePhoto(pendingDeletePhoto.id)}
            onCancel={() => setPendingDeletePhoto(undefined)}
          />
        )}

      {pendingMovePhoto &&
        renderModal(
          <MovePhotoModal
            photo={pendingMovePhoto}
            collections={collections}
            onConfirm={handleConfirmMovePhoto}
            onCancel={() => setPendingMovePhoto(undefined)}
          />
        )}
    </React.Fragment>
  );
}

export default SingleCollectionManager;
